import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { openSalesStream } from '../../lib/salesStream';
import { desktopNotify } from '../../lib/desktopNotify';
import IncomingCallCard from './IncomingCallCard';
import { callerTitle } from '../../utils/calls';
import type { CallCardData } from '../../types/sales';

/**
 * Mounted once in App. For someone with a Sales seat it keeps the incoming-
 * call stream open and shows a card for each call ringing for them (their
 * extension, or the sales group), with a desktop notification when they
 * turned those on. A card goes when the call is answered, missed or ends,
 * when dismissed, or after three minutes.
 */

const CARD_MAX_MS = 3 * 60 * 1000;

export default function IncomingCallWatcher() {
  const { user } = useAuth();
  const { hasAccess } = useModuleLicense('sales');
  const navigate = useNavigate();
  const [cards, setCards] = useState<CallCardData[]>([]);
  const shown = useRef(new Set<string>());

  const dismiss = useCallback((uuid: string) => {
    setCards((list) => list.filter((c) => c.call_uuid !== uuid));
  }, []);

  const ring = useCallback((call: CallCardData) => {
    setCards((list) => [...list.filter((c) => c.call_uuid !== call.call_uuid), call]);
    if (!shown.current.has(call.call_uuid)) {
      shown.current.add(call.call_uuid);
      const title = callerTitle(call);
      desktopNotify('Incoming call', call.caller_number && title !== call.caller_number ? `${title} · ${call.caller_number}` : title,
        `call-${call.call_uuid}`, () => { if (call.customer_id) navigate(`/accounts/customers/${call.customer_id}`); });
    }
    window.setTimeout(() => dismiss(call.call_uuid), CARD_MAX_MS);
  }, [dismiss, navigate]);

  useEffect(() => {
    if (!user || !hasAccess) return undefined;
    return openSalesStream({
      onReady: (ringing) => ringing.forEach(ring),
      onCall: (call) => {
        if (call.status === 'ringing' && call.for_me && call.direction === 'inbound') ring(call);
        else if (call.status !== 'ringing') dismiss(call.call_uuid);
      },
    });
  }, [user, hasAccess, ring, dismiss]);

  if (!cards.length) return null;
  return (
    <div className="fixed z-[60] top-20 left-4 right-4 sm:left-auto sm:w-96 space-y-2 pointer-events-none">
      {cards.map((c) => <IncomingCallCard key={c.call_uuid} call={c} onDismiss={() => dismiss(c.call_uuid)} />)}
    </div>
  );
}
